import React, { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
    calculateTagesstunden,
    calculateWochenstundenFromPlan,
    createArbeitszeitEntryFromPrevious,
    formatStdForDisplay,
    isArbeitszeitEntryComplete,
    normalizeTypedTime,
    shiftTimeValue,
    type ArbeitsRhythmus,
    type ArbeitszeitTagEntry,
    WOCHENTAG_OPTIONS,
} from '../../../utils/pe-papers/arbeitszeitPlan';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const MINUS_BTN_CLASS =
    'flex items-center justify-center w-11 h-11 shrink-0 rounded-lg border border-slate-600 bg-slate-800 text-slate-200 text-xl font-black leading-none hover:border-red-400 hover:text-red-300 hover:bg-slate-900 transition-colors';

const PLUS_BTN_CLASS =
    'flex items-center justify-center w-11 h-11 shrink-0 rounded-lg border border-[#E6C299]/70 bg-[#E6C299]/20 text-[#E6C299] text-xl font-black leading-none hover:bg-[#E6C299]/30 hover:border-[#E6C299] transition-colors';

const VALUE_INPUT_CLASS =
    'w-full min-w-[4.75rem] h-11 rounded-lg border border-pe-border dark:border-slate-600 bg-white dark:bg-[#0b0d12] text-pe-text dark:text-white text-center text-base font-semibold tabular-nums px-1 focus:outline-none focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299]/30';

/** Einfachklick/Fokus: alles markieren zum Überschreiben. Doppelklick: Cursor für Feinjustierung. */
const selectAllOnSingleActivate = (
    e: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>
) => {
    const el = e.currentTarget;
    const detail = 'detail' in e ? e.detail : 1;
    if (detail > 1) return;
    requestAnimationFrame(() => {
        if (document.activeElement === el) el.select();
    });
};

export interface ArbeitszeitPlanEditorProps {
    title: string;
    plan: ArbeitszeitTagEntry[];
    onChange: (plan: ArbeitszeitTagEntry[]) => void;
    required?: boolean;
    isFieldInvalid?: (field: string) => boolean;
    onClearError?: (field: string) => void;
    emptyHint?: string;
    /** Für Monats-Rhythmus-Umrechnung (Default 4,33). */
    wochensatz?: number;
    /** Soll-Wochenstunden (z. B. Feld „Neue Wochenstunden“) — Ist-Summe wird dagegen gehalten. */
    targetWochenstunden?: number | null;
}

const TimeStepper: React.FC<{
    value: string;
    onChange: (value: string) => void;
    stepMinutes?: number;
}> = ({ value, onChange, stepMinutes = 15 }) => {
    const [draft, setDraft] = useState(value);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    const commitDraft = () => {
        const normalized = normalizeTypedTime(draft);
        if (normalized === null) {
            setDraft(value);
            return;
        }
        onChange(normalized);
        setDraft(normalized);
    };

    return (
        <div className="flex items-center gap-1.5">
            <button
                type="button"
                onClick={() => onChange(shiftTimeValue(value, -stepMinutes))}
                className={MINUS_BTN_CLASS}
                aria-label="15 Minuten früher"
                title="15 Minuten früher"
            >
                −
            </button>
            <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="09:00"
                value={draft}
                onFocus={selectAllOnSingleActivate}
                onClick={selectAllOnSingleActivate}
                onChange={(e) => {
                    const next = e.target.value;
                    setDraft(next);
                    const lookalike = next.trim().replace(',', ':').replace('.', ':');
                    const complete = /^\d{1,2}:\d{2}$/.test(lookalike) || /^\d{4}$/.test(lookalike.replace(':', ''));
                    if (!complete) return;
                    const normalized = normalizeTypedTime(next);
                    if (normalized) onChange(normalized);
                }}
                onBlur={commitDraft}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commitDraft();
                    }
                }}
                className={VALUE_INPUT_CLASS}
            />
            <button
                type="button"
                onClick={() => onChange(shiftTimeValue(value, stepMinutes))}
                className={PLUS_BTN_CLASS}
                aria-label="15 Minuten später"
                title="15 Minuten später"
            >
                +
            </button>
        </div>
    );
};

const PauseStepper: React.FC<{
    value: string;
    onChange: (value: string) => void;
}> = ({ value, onChange }) => {
    const current = Number(value);
    const base = Number.isFinite(current) && current > 0 ? current : 0;
    const apply = (next: number) => onChange(String(Math.max(0, Math.round(next))));

    return (
        <div className="flex items-center gap-1.5">
            <button
                type="button"
                onClick={() => apply(base - 5)}
                className={MINUS_BTN_CLASS}
                aria-label="Pause 5 Minuten kürzer"
                title="Pause 5 Minuten kürzer"
            >
                −
            </button>
            <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="30"
                value={value}
                onFocus={selectAllOnSingleActivate}
                onClick={selectAllOnSingleActivate}
                onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d]/g, '');
                    onChange(raw);
                }}
                className={VALUE_INPUT_CLASS}
            />
            <button
                type="button"
                onClick={() => apply(base + 5)}
                className={PLUS_BTN_CLASS}
                aria-label="Pause 5 Minuten länger"
                title="Pause 5 Minuten länger"
            >
                +
            </button>
        </div>
    );
};

const ArbeitszeitPlanEditor: React.FC<ArbeitszeitPlanEditorProps> = ({
    title,
    plan,
    onChange,
    required = false,
    isFieldInvalid = () => false,
    onClearError,
    emptyHint,
    wochensatz = 4.33,
    targetWochenstunden = null,
}) => {
    const updateEntry = (entryId: string, patch: Partial<ArbeitszeitTagEntry>) => {
        onChange(plan.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry)));
        onClearError?.('arbeitsplan');
        onClearError?.('neuer_arbeitsplan');
    };

    const addDay = () => {
        const previous = plan[plan.length - 1];
        onChange([...plan, createArbeitszeitEntryFromPrevious(previous)]);
        onClearError?.('arbeitsplan');
        onClearError?.('neuer_arbeitsplan');
    };

    const summeWochenstunden = useMemo(
        () => calculateWochenstundenFromPlan(plan, wochensatz),
        [plan, wochensatz]
    );
    const completeCount = useMemo(() => plan.filter(isArbeitszeitEntryComplete).length, [plan]);
    const target =
        typeof targetWochenstunden === 'number' && Number.isFinite(targetWochenstunden) && targetWochenstunden > 0
            ? targetWochenstunden
            : null;
    const diff = target != null ? summeWochenstunden - target : null;
    const matchTol = 0.05;
    const sumTone =
        target == null || summeWochenstunden <= 0
            ? 'border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-text dark:text-slate-100'
            : Math.abs(diff ?? 0) <= matchTol
              ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
              : (diff ?? 0) < 0
                ? 'border-amber-500/50 bg-amber-500/10 text-amber-900 dark:text-amber-200'
                : 'border-red-500/40 bg-red-500/10 text-red-800 dark:text-red-300';

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-pe-text dark:text-white">{title}</p>
                <button
                    type="button"
                    onClick={addDay}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/10"
                >
                    + Tag hinzufügen
                </button>
            </div>
            <p className="text-[10px] text-pe-muted dark:text-slate-500 leading-relaxed">
                Uhrzeiten direkt eintippen (z. B. 9:00 oder 0900) oder mit − / + in 15-Minuten-Schritten anpassen.
                Pause frei in Minuten oder ±5. Nächster Tag übernimmt die Werte vom Vortag.
            </p>

            <div className={`rounded-xl border px-3 py-2.5 sticky top-0 z-10 ${sumTone}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                        Summe aus Tagesplan
                    </p>
                    <p className="text-lg font-black tabular-nums tracking-tight">
                        {summeWochenstunden > 0
                            ? `${formatStdForDisplay(summeWochenstunden)} Std./Woche`
                            : '— Std./Woche'}
                    </p>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-snug opacity-90">
                    <span>
                        {completeCount}/{plan.length || 0} Tag(e) vollständig
                    </span>
                    {target != null ? (
                        <>
                            <span>
                                Soll: <strong>{formatStdForDisplay(target)} Std./Woche</strong>
                            </span>
                            {summeWochenstunden > 0 ? (
                                <span>
                                    {Math.abs(diff ?? 0) <= matchTol
                                        ? 'Passt zum Soll'
                                        : (diff ?? 0) < 0
                                          ? `noch ${(Math.abs(diff ?? 0)).toFixed(2).replace('.', ',')} Std. fehlen`
                                          : `${(diff ?? 0).toFixed(2).replace('.', ',')} Std. über Soll`}
                                </span>
                            ) : (
                                <span>Tage eintragen – Summe aktualisiert sich live</span>
                            )}
                        </>
                    ) : (
                        <span>Live-Summe aus Von/Bis/Pause (ohne Pause)</span>
                    )}
                </div>
            </div>

            {plan.length === 0 && !required && (
                <p className="text-xs text-pe-muted dark:text-slate-500">
                    {emptyHint || 'Optional – über „Tag hinzufügen“ eintragen.'}
                </p>
            )}
            {plan.map((entry, index) => {
                const rowErrorId = `neuer_arbeitsplan_${index}`;
                const tagesStd = calculateTagesstunden(entry);
                return (
                    <div
                        key={entry.id}
                        className={`space-y-3 p-3 rounded-lg border ${
                            isFieldInvalid(rowErrorId) || (required && isFieldInvalid('neuer_arbeitsplan')) || (required && isFieldInvalid('arbeitsplan'))
                                ? 'border-red-500/60'
                                : 'border-pe-border dark:border-slate-700'
                        } bg-pe-card dark:bg-[#15171e]`}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                            <div>
                                <label className={LABEL_CLASS}>Wochentag</label>
                                <select
                                    value={entry.wochentag}
                                    onChange={(e) =>
                                        updateEntry(entry.id, {
                                            wochentag: e.target.value as ArbeitszeitTagEntry['wochentag'],
                                        })
                                    }
                                    className={INPUT_CLASS}
                                >
                                    {WOCHENTAG_OPTIONS.map((opt) => (
                                        <option key={opt.key} value={opt.key}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={LABEL_CLASS}>Von</label>
                                <TimeStepper value={entry.von} onChange={(von) => updateEntry(entry.id, { von })} />
                            </div>
                            <div>
                                <label className={LABEL_CLASS}>Bis</label>
                                <TimeStepper value={entry.bis} onChange={(bis) => updateEntry(entry.id, { bis })} />
                            </div>
                            <div>
                                <label className={LABEL_CLASS}>Pause (Min.)</label>
                                <PauseStepper
                                    value={entry.pauseMinuten}
                                    onChange={(pauseMinuten) => updateEntry(entry.id, { pauseMinuten })}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_0.7fr_auto] gap-2 items-end">
                            <div>
                                <label className={LABEL_CLASS}>Rhythmus</label>
                                <select
                                    value={entry.rhythmus}
                                    onChange={(e) =>
                                        updateEntry(entry.id, {
                                            rhythmus: e.target.value as ArbeitsRhythmus,
                                            tageProMonat: e.target.value === 'monatlich' ? entry.tageProMonat || '1' : '',
                                        })
                                    }
                                    className={INPUT_CLASS}
                                >
                                    <option value="woechentlich">Jede Woche</option>
                                    <option value="monatlich">Nur X Tage pro Monat</option>
                                </select>
                            </div>
                            {entry.rhythmus === 'monatlich' && (
                                <div>
                                    <label className={LABEL_CLASS}>Tage / Monat (1–3)</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={entry.tageProMonat}
                                        onChange={(e) =>
                                            updateEntry(entry.id, { tageProMonat: e.target.value.replace(/[^\d]/g, '') })
                                        }
                                        className={INPUT_CLASS}
                                    />
                                </div>
                            )}
                            <div className="flex items-center justify-end gap-2">
                                {tagesStd > 0 && (
                                    <span className="text-xs text-pe-accent-text dark:text-[#E6C299]">
                                        {tagesStd.toFixed(2).replace('.', ',')} Std./Tag
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onChange(plan.filter((row) => row.id !== entry.id))}
                                    disabled={required && plan.length <= 1}
                                    className="p-2 rounded-lg border border-pe-border dark:border-slate-700 text-pe-muted hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                                    aria-label="Eintrag entfernen"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
            {required && isFieldInvalid('neuer_arbeitsplan') && (
                <p className="text-[10px] text-red-400 mt-1">Mindestens ein vollständiger Tageseintrag ist erforderlich</p>
            )}
            {required && isFieldInvalid('arbeitsplan') && (
                <p className="text-[10px] text-red-400 mt-1">Mindestens ein vollständiger Tageseintrag ist erforderlich</p>
            )}
        </div>
    );
};

export default ArbeitszeitPlanEditor;
